import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Paper, Typography, styled, Grid, Box, CircularProgress } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import TanstackReactTable from 'core/table/TanstackReactTable';

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 600,
  color: '#19273C',
  marginBottom: '16px',
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  height: '100%',
}));

const CenterText = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center',
  pointerEvents: 'none',
}));

const CenterLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: '#425166',
  marginBottom: '4px',
}));

const CenterValue = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 700,
  color: '#19273C',
}));

const StatusColor = styled(Box)(({ color }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: color,
  marginRight: '12px',
}));

const StatusCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const StatusName = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#404040',
}));

const CountCell = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#19273C',
}));

const COLORS = {
  completed: '#079A8A',
  rejected: '#7A5195',
  expired: '#970A01',
  processing: '#3994FF',
  pending: '#F59E0B',
  created: '#BC5090',
};

export default function KycStat() {
  const { isLoading, data, error } = useSelector((state) => state.dashboard.kycCount);
  const [columnVisibility] = useState({});
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    if (data) {
      setChartKey((prev) => prev + 1);
    }
  }, [data]);

  const kycData = data?.data?.data || null;

  const chartData = useMemo(() => {
    if (!kycData) return [];

    const dataArray = [
      {
        name: 'Completed',
        value: kycData.completed_count || 0,
        color: COLORS.completed,
      },
      {
        name: 'Rejected',
        value: kycData.rejected_count || 0,
        color: COLORS.rejected,
      },
      {
        name: 'Expired',
        value: kycData.expired_count || 0,
        color: COLORS.expired,
      },
      {
        name: 'Processing',
        value: kycData.processing_count || 0,
        color: COLORS.processing,
      },
      {
        name: 'Pending',
        value: kycData.pending_count || 0,
        color: COLORS.pending,
      },
      {
        name: 'Created',
        value: kycData.created_count || 0,
        color: COLORS.created,
      },
    ];

    const filtered = dataArray.filter((item) => item.value > 0);
    return filtered;
  }, [kycData]);

  const totalCount = useMemo(() => kycData?.total_count || 0, [kycData]);

  const displayChartData = useMemo(() => {
    if (totalCount === 0 || chartData.length === 0) {
      return [{ name: 'Empty', value: 1, color: '#E5E5E5' }];
    }
    return chartData;
  }, [totalCount, chartData]);

  const tableData = useMemo(() => {
    if (!kycData) return [];

    return [
      {
        status: 'Completed',
        count: kycData.completed_count || 0,
        color: COLORS.completed,
        removeBorder: true,
      },
      {
        status: 'Rejected',
        count: kycData.rejected_count || 0,
        color: COLORS.rejected,
        removeBorder: true,
      },
      {
        status: 'Expired',
        count: kycData.expired_count || 0,
        color: COLORS.expired,
        removeBorder: true,
      },
      {
        status: 'Processing',
        count: kycData.processing_count || 0,
        color: COLORS.processing,
        removeBorder: true,
      },
      {
        status: 'Pending',
        count: kycData.pending_count || 0,
        color: COLORS.pending,
        removeBorder: true,
      },
      {
        status: 'Created',
        count: kycData.created_count || 0,
        color: COLORS.created,
        removeBorder: true,
      },
    ];
  }, [kycData]);

  const columns = useMemo(
    () => [
      {
        header: 'KYC Status',
        accessorFn: (row) => row.status,
        cell: (info) => {
          const row = info.row.original;
          return (
            <StatusCell>
              <StatusColor color={row.color} />
              <StatusName>{row.status}</StatusName>
            </StatusCell>
          );
        },
      },
      {
        header: 'Total Count',
        accessorFn: (row) => row.count,
        meta: {
          align: 'right',
        },
        cell: (info) => (
          <Box sx={{ textAlign: 'right' }}>
            <CountCell>{info.getValue()?.toLocaleString() || '0'}</CountCell>
          </Box>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: { columnVisibility },
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Paper sx={{ p: '20px', borderRadius: '16px', height: '100%' }}>
      <Title>KYC Details</Title>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={6.5}>
            <ChartContainer>
              <ResponsiveContainer width="100%" height="100%" key={`chart-${chartKey}`}>
                <PieChart>
                  <Pie
                    data={displayChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={90}
                    outerRadius={130}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                    isAnimationActive={true}
                    animationDuration={1000}
                    animationBegin={0}
                  >
                    {displayChartData.map((entry, index) => (
                      <Cell key={`cell-${index}-${entry.value}-${entry.name}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <CenterText>
                <CenterLabel>Total Customer</CenterLabel>
                <CenterValue>{totalCount.toLocaleString()}</CenterValue>
              </CenterText>
            </ChartContainer>
          </Grid>

          <Grid item xs={12} md={12} lg={5.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', minHeight: '280px' }}>
              <TanstackReactTable
                table={table}
                isLoading={isLoading}
                data={tableData}
                columnsCount={columns.length}
                noHeaderBackground
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}
